import { type ReactNode, useRef, useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { TabBar, TabItem } from '@/components/TabBar'
import { ProgressBar } from '@/components/ProgressBar'
import { IconButton } from '@/components/IconButton'
import styles from './MobileJob.module.css'

type MobileTab = 'chat' | 'documents' | 'details' | 'consultation'

export interface MobileJobProps {
  children?: ReactNode
}

export function MobileJob({ children }: MobileJobProps) {
  return <div className={styles.root}>{children}</div>
}

export interface MobileJobBodyProps {
  hasConsultation: boolean
  header: ReactNode
  chat: ReactNode
  documents: ReactNode
  details: ReactNode
  consultation: ReactNode
}

export function MobileJobBody({
  hasConsultation,
  header,
  chat,
  documents,
  details,
  consultation,
}: MobileJobBodyProps) {
  const [tab, setTab] = useState<MobileTab>('chat')

  return (
    <>
      <div className={styles.topChrome}>
        <MobileJobHeader>{header}</MobileJobHeader>

        <div className={styles.tabScroller}>
          <TabBar bordered align="left" stretch centerText>
            <TabItem
              active={tab === 'chat'}
              onPress={() => setTab('chat')}
              testId="mobile-tab-chat"
            >
              Chat
            </TabItem>
            <TabItem
              active={tab === 'documents'}
              onPress={() => setTab('documents')}
              testId="mobile-tab-documents"
            >
              Documents
            </TabItem>
            <TabItem
              active={tab === 'details'}
              onPress={() => setTab('details')}
              testId="mobile-tab-details"
            >
              Details
            </TabItem>
            {hasConsultation && (
              <TabItem
                active={tab === 'consultation'}
                onPress={() => setTab('consultation')}
                testId="mobile-tab-consultation"
              >
                Consultation
              </TabItem>
            )}
          </TabBar>
        </div>
      </div>

      {tab === 'chat' ? (
        chat
      ) : (
        <main className={styles.tabBody}>
          {tab === 'documents' && documents}
          {tab === 'details' && details}
          {tab === 'consultation' && hasConsultation && consultation}
        </main>
      )}
    </>
  )
}

interface MobileJobHeaderProps {
  children: ReactNode
}

function MobileJobHeader({ children }: MobileJobHeaderProps) {
  return <header className={styles.header}>{children}</header>
}

export interface MobileJobProgressionSummaryProps {
  title?: string
  completionStatus: number
  pendingCompletionStatus: number
}

export function MobileJobProgressionSummary({
  title,
  completionStatus,
  pendingCompletionStatus,
}: MobileJobProgressionSummaryProps) {
  return (
    <div
      className={styles.progressionSummary}
      data-test="mobile-job-progression-summary"
    >
      <div className={styles.progressionTitle}>{title}</div>
      <ProgressBar
        bars={[
          {
            progress: completionStatus,
          },
          {
            progress: pendingCompletionStatus,
            style: 'striped',
          },
        ]}
      />
    </div>
  )
}

export interface MobileChatTabProps {
  children?: ReactNode
}

export function MobileChatTab({ children }: MobileChatTabProps) {
  return <div className={styles.chatTab}>{children}</div>
}

export interface MobileConversationProps {
  readOnly: boolean
  messages: ReactNode
  composer: ReactNode
  typingIndicator: ReactNode
}

export function MobileConversation({
  readOnly,
  messages,
  composer,
  typingIndicator,
}: MobileConversationProps) {
  return (
    <section className={styles.chatTab}>
      <div className={styles.messages}>{messages}</div>
      {!readOnly && (
        <div className={styles.composerArea}>
          {composer}
          {typingIndicator}
        </div>
      )}
    </section>
  )
}

export interface MobileConversationComposerProps {
  composerText: string
  onComposerTextChange: (text: string) => unknown
  onSend: () => unknown
}

export function MobileConversationComposer({
  composerText,
  onComposerTextChange,
  onSend,
}: MobileConversationComposerProps) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  return (
    <div className={styles.composer}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- Mouse-only convenience that widens the textarea's hit area; keyboard users focus the textarea directly. */}
      <div
        className={styles.inputContainer}
        onClick={() => textareaRef.current?.focus()}
      >
        <TextareaAutosize
          className={styles.input}
          ref={textareaRef}
          data-private
          data-test="conversation-input"
          style={{ resize: 'none' }}
          placeholder="Write your message here."
          value={composerText}
          maxRows={8}
          onChange={(e) =>
            onComposerTextChange((e.target as HTMLTextAreaElement).value)
          }
        />
      </div>
      <IconButton
        name="chat-submit"
        label="Send message"
        variant="ghost"
        isDisabled={!composerText.trim()}
        onPress={onSend}
        testId="conversation-send-mobile"
        size={32}
      />
    </div>
  )
}

export interface MobileDetailsProps {
  children?: ReactNode
}

export function MobileDetails({ children }: MobileDetailsProps) {
  return <div className={styles.details}>{children}</div>
}
